/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { IOrderDetailsProps, IOrderDetailsResources } from '../definition-extensions/order-details.ext.props.autogenerated';

export const OrderInfomation: React.FC<IOrderInformation & { resources: IOrderDetailsResources, salesOrder?: SalesOrder }> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress,
    salesOrder,
    resources
}) => {
    // @ts-ignore
    const customTrackingUrl = salesOrder?.ExtensionProperties?.filter((tracking: { Key: string }) => tracking.Key === 'tranckingURL_CAP')[0]?.Value.StringValue;
    // @ts-ignore
    const customTrackingCarrier = salesOrder?.ExtensionProperties?.filter((tracking: { Key: string }) => tracking.Key === 'tranckingCarrier_CAP')[0]?.Value.StringValue;

    return (
        <React.Fragment>
            <Node {...orderInformationProps}>
                {channelName}
                {channelAddress}
                {salesId}
                {receiptId}
                {createdDate}
                {count}
                {amount}
                {channelReferenceId}
            </Node>
            {customTrackingUrl && <Node className='ms-order-details__tracking'>
                <div className="ms-order-details__tracking-title">{resources.shippingInformation}</div>
                <div className="ms-order-details__tracking-row">
                    <p className="ms-order-details__tracking-courier">{resources.shippedWith} {customTrackingCarrier}</p>
                    <a href={customTrackingUrl} target='_blank'><button className="msc-cta__primary ms-order-details__tracking-button">{resources.trackTheOrder}</button></a>
                </div>
            </Node>}
        </React.Fragment>
    )
};

export const SalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
    </Node>
);

export const GroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count, trackingInfo, shippedStatusCheck }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {/* shippedStatusCheck === 'shipped' ? null : trackingInfo */}
    </Node>
);

export const Group: React.FC<IGroup> = ({ groupProps, delivery, address, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <GroupDelivery {...delivery} />}
        <Node className='ms-order-details__sales-lines_container'>
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map(salesLine => (
                        <React.Fragment key={salesLine.data.salesLine.LineId}>
                            {salesLine.data.deliveryType === 'ship' && salesLine.data.shipment ? salesLine.trackingInfo : null}
                            <SalesLine {...salesLine} />
                            {salesLine.salesStatus}
                        </React.Fragment>
                    ))}
                </Node>
            )}
            {!delivery.showTimeslot && address}
            {delivery.showTimeslot && (
                <Node {...delivery.pickupProps}>
                    {address}
                    {delivery.pickupDateTimeslot}
                </Node>
            )}
        </Node>
    </Node>
);

export const Groups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <Group key={index} {...group} />
        ))}
    </Node>
);

const _renderCustomTaxLine = (taxAmount: number): JSX.Element | null => {
    //const { estimatedTaxLabel, yetToBeCalculatedText} = props.resources;

    return (
        <div style={{ marginBottom: '1.25rem'}}>
            <p className="ms-order-details__order-summary-line-tax-amount">
                <span className="ms-order-details__order-summary-label">Tax</span>
                <span className="msc-price ms-order-details__order-summary-price">
                    <span className="msc-price__actual" itemProp="price">{taxAmount}</span>
                </span>
            </p>
        </div>
    )
}

export const OrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    ...other
}) => {
    // @ts-ignore
    const locale = other.locale;
    // @ts-ignore
    const taxAmount = other.taxAmount;
    
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {shipping}
            {locale.toLowerCase() == 'en-gb' && taxAmount > 0 ? _renderCustomTaxLine(taxAmount) : tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const Payment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const Help: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsProps<IOrderDetailsViewProps>> = ({
    salesOrder,
    moduleProps,
    heading,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    resources
}) => {
    // prettier-ignore
    // @ts-ignore
    const customTrackingNumber = salesOrder?.ExtensionProperties?.filter((tracking: { Key: string }) => tracking.Key === 'trackingId_CAP')[0]?.Value.StringValue;
    // prettier-ignore
    // @ts-ignore
    const customTrackingStatus = salesOrder?.ExtensionProperties?.filter((tracking: { Key: string }) => tracking.Key === 'trakingStatus_CAP')[0]?.Value.StringValue;
    const locale = moduleProps.moduleProps.context.request.locale;
    // @ts-ignore
    const salesOrderData = moduleProps?.moduleProps?.data?.orderHydration?.result?.salesOrder;
    const taxAmount = salesOrderData?.TaxAmount;
    const extraOrdersSummaryEu = {
        locale: locale,
        taxAmount: taxAmount
    };

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderInfomation resources={resources} salesOrder={salesOrder} {...orderInfomation} />}
            {groups && <Groups {...groups} />}
            <Node className='ms-order-details__order-summary-container'>
                {orderSummary && <OrderSummary {...orderSummary} {...extraOrdersSummaryEu} />}
                {payment && <Payment {...payment} />}
            </Node>
            {help && <Help {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
